import "styles/pages/page-all-vehicles.scss";
import React, { useState, useEffect } from "react";
import { graphql } from "gatsby";
import _ from "lodash";
import queryString from "query-string";

import Layout from "components/Layout";
import Seo from "components/Seo";
import SubpageHeader from "components/SubpageHeader/SubpageHeader";
import Select from "components/Select/Select";
import GridViewSwitch from "components/GridViewSwitch/GridViewSwitch";
import Filters from "components/Filters/Filters";
import VehicleTile from "components/VehicleTile/VehicleTile";
import VehicleTileHorizontal from "components/VehicleTileHorizontal/VehicleTileHorizontal";
import PaginationUniversal from "components/PaginationUniversal/PaginationUniversal";
import { NumberParam, useQueryParam } from "use-query-params";

const PageAllVehicles = ({ data, location }) => {
	const { used } = queryString.parse(location.search);
	const vehicles = data.vehicles.edges;
	const trailers = data.trailers.edges;
	const vehiclesBrands = data.allWpVehicleBrand.nodes;
	const trailersBrands = data.allWpTrailerBrand.nodes;
	const allBrands = [...vehiclesBrands, ...trailersBrands];
	const allVehicles = [...vehicles, ...trailers];
	const [view, setView] = useState("grid");
	const [available, setAvailable] = useState([]);
	const [types, setTypes] = useState([]);
	const [conditions, setConditions] = useState([]);
	const [buildings, setBuildings] = useState([]);
	const [selectedBrands, setSelectedBrands] = useState([]);
	const [price, setPrice] = useState({ min: 0, max: 900 });
	const [skip, setSkip] = useQueryParam("skip", NumberParam);
	const [postsPerPage, setPostsPerPage] = useState(9);
	const [sortKey, setSortKey] = useState(null);
	const [sortDirection, setSortDirection] = useState(true);
	const [newVehicles, setNewVehicles] = useState(allVehicles);
	const [brandsCounter, setBrandsCounter] = useState({});
	const _skip = skip || 0;

	//Filtered vehicles
	const newFilteredVehicles = allVehicles.filter((allVehicles) => {
		return (
			(available.length === 0 ||
				_.includes(available, allVehicles.node.vehicles.available)) &&
			(types.length === 0 ||
				_.includes(types, allVehicles.node.vehicleType.vehicleType)) &&
			(conditions.length === 0 ||
				_.includes(conditions, allVehicles.node.vehicles.used)) &&
			(buildings.length === 0 ||
				_.includes(buildings, allVehicles.node.vehicles.type[0])) &&
			(selectedBrands.length === 0 ||
				_.includes(
					selectedBrands,
					allVehicles.node.vehicles.category?.name
				)) &&
			allVehicles.node.vehicles.price >= price.min * 1000
		);
	});

	//Remove duplicated brands from allBrands (vehicles+trailers)
	const sortedBrands = allBrands.filter(
		(elem, index, self) =>
			self.findIndex((t) => {
				return t.name === elem.name;
			}) === index
	);

	useEffect(() => {
		setNewVehicles(newFilteredVehicles);
	}, [
		types,
		conditions,
		buildings,
		available,
		selectedBrands,
		location,
		price,
	]);

	useEffect(() => {
		if (used) {
			setConditions(["used"]);
		} else {
			setConditions([]);
		}
	}, [used]);

	//PAGINATION
	let _data = newVehicles;

	// Sorted by available/sold
	const _dataSortedAvailable = _data.filter(
		(item) => item.node.vehicles.labelName !== "Sprzedane"
	);
	const _dataSortedSold = _data.filter(
		(item) => item.node.vehicles.labelName === "Sprzedane"
	);

	const _dataSorted = [..._dataSortedAvailable, ..._dataSortedSold];

	//
	const _dataForPage = _dataSorted.slice(_skip, _skip + postsPerPage);
	const currentPage = _skip / postsPerPage + 1;
	const numPages = Math.max(1, Math.ceil(_data.length / postsPerPage));
	const onPrevClick = () => {
		setSkip((currentPage - 2) * postsPerPage);
	};
	const onNextClick = () => {
		setSkip(currentPage * postsPerPage);
	};
	const onItemClick = (page) => {
		setSkip((page - 1) * postsPerPage);
	};
	const handlePage = () => {
		setSkip(0);
	};
	const handleSkip = (event) => {
		setPostsPerPage(event.target.value);
	};

	//VIEW
	const setGridView = () => {
		setView("grid");
	};
	const setListView = () => {
		setView("list");
	};

	//SORTING
	const sortVehicles = (event) => {
		setSortKey(event.target.value);
		if (event.target.value !== null) {
			if (sortDirection) {
				newVehicles.sort((a, b) =>
					a.node.vehicles[event.target.value] >
					b.node.vehicles[event.target.value]
						? 1
						: -1
				);
			} else {
				newVehicles.sort((a, b) =>
					a.node.vehicles[event.target.value] >
					b.node.vehicles[event.target.value]
						? -1
						: 1
				);
			}
			setSortDirection(!sortDirection);
		}
	};

	//FILTERS
	const handleTypes = (type) => {
		if (types && types.includes(type)) {
			setTypes(types.filter((t) => t !== type));
		} else {
			setTypes([...types, type]);
		}
	};

	const handleConditions = (condition) => {
		if (conditions && conditions.includes(condition)) {
			setConditions(conditions.filter((c) => c !== condition));
		} else {
			setConditions([...conditions, condition]);
		}
	};

	const handleBuildings = (building) => {
		if (buildings && buildings.includes(building)) {
			setBuildings(buildings.filter((b) => b !== building));
		} else {
			setBuildings([...buildings, building]);
		}
	};

	const handleBrands = (brand) => {
		if (selectedBrands && selectedBrands.includes(brand)) {
			setSelectedBrands(selectedBrands.filter((b) => b !== brand));
		} else {
			setSelectedBrands([...selectedBrands, brand]);
		}
	};

	const handlePrice = (price) => {
		setPrice(price);
	};

	const newVehiclesLength = allVehicles.filter((el) => {
		return el.node.vehicles.used === "new";
	});

	const usedVehiclesLength = allVehicles.filter((el) => {
		return el.node.vehicles.used === "used";
	});

	const kamperVanLength = allVehicles.filter((el) => {
		return el.node.vehicles.type[0] === "kampervan";
	});

	const polIntegraLength = allVehicles.filter((el) => {
		return el.node.vehicles.type[0] === "polintegra";
	});

	const integraLength = allVehicles.filter((el) => {
		return el.node.vehicles.type[0] === "integra";
	});

	const alkowyLength = allVehicles.filter((el) => {
		return el.node.vehicles.type[0] === "alkowy";
	});

	return (
		<Layout>
			<Seo title="Pojazdy" />
			<SubpageHeader
				location="Pojazdy"
				title="Pojazdy dostępne od ręki w Krakowie (nowe i używane)"
				isVehiclesPage={true}
			/>
			<section className="all-vehicles">
				<div className="container">
					<div className="row">
						<div className="col-lg-9">
							<div className="top-bar">
								<h3 className="top-bar__title">
									Pojazdy dostępne od ręki w Krakowie
								</h3>
								<div className="top-bar__right-buttons">
									<Select
										placeholder="Sortowanie"
										name="sortSelect"
										id="sortSelect"
										options={[
											{
												label: "Cena od najniższej",
												value: "price",
											},
											{
												label: "Cena od najwyższej",
												value: "price",
											},
										]}
										onChange={(e) => sortVehicles(e)}
									/>
									<Select
										placeholder="Liczba wyników"
										options={[
											{ label: 9, value: 9 },
											{ label: 12, value: 12 },
											{ label: 15, value: 15 },
											{ label: 18, value: 18 },
											{
												label: "Pokaż wszystkie",
												value: 999,
											},
										]}
										onChange={(e) => handleSkip(e)}
									/>
									<GridViewSwitch
										onGridClick={setGridView}
										onListClick={setListView}
									/>
								</div>
							</div>
						</div>
					</div>
					<div className="row">
						<div className="col-lg-9">
							<div className="row">
								{_dataForPage?.map((node, index) => {
									const vehicle = node.node.vehicles
										? node.node.vehicles
										: node.node.trailers;
									return view === "list" ? (
										<div className="col-md-12" key={index}>
											<VehicleTileHorizontal
												thumbnail={
													vehicle.gallery &&
													vehicle?.gallery[0]
														?.sourceUrl
												}
												name={vehicle?.name}
												price={vehicle?.price}
												discountPrice={
													vehicle?.priceDiscount
												}
												discount={vehicle?.discount}
												label={vehicle?.showLabel}
												labelColor={vehicle?.labelColor}
												labelName={vehicle?.labelName}
												used={vehicle?.used}
												type={vehicle?.type[0]}
												url={node?.node?.uri}
												desc={vehicle?.shortDesc}
												currency={vehicle?.currency}
											/>
										</div>
									) : (
										<div
											className="col-md-4 col-sm-6"
											key={index}
										>
											<VehicleTile
												thumbnail={
													vehicle.gallery &&
													vehicle?.gallery[0]
														?.sourceUrl
												}
												name={vehicle?.name}
												price={vehicle?.price}
												discountPrice={
													vehicle?.priceDiscount
												}
												discount={vehicle?.discount}
												label={vehicle?.showLabel}
												labelColor={vehicle?.labelColor}
												labelName={vehicle?.labelName}
												used={vehicle?.used}
												type={vehicle?.type[0]}
												url={node?.node?.uri}
												desc={vehicle?.shortDesc}
												currency={vehicle?.currency}
											/>
										</div>
									);
								})}
							</div>
							{!!_dataForPage?.length && (
								<PaginationUniversal
									currentPage={currentPage}
									numPages={numPages}
									onPrevClick={onPrevClick}
									onNextClick={onNextClick}
									onItemClick={onItemClick}
								/>
							)}
							{!_dataForPage?.length && (
								<div>
									Brak pojazdów dla podanego wyszukiwania
								</div>
							)}
						</div>
						<div className="col-lg-3">
							<Filters
								handleTypes={handleTypes}
								handleConditions={handleConditions}
								handleBuildings={handleBuildings}
								handlePrice={handlePrice}
								handleBrands={handleBrands}
								vehiclesLength={vehicles.length}
								trailersLength={trailers.length}
								newVehiclesLength={newVehiclesLength.length}
								usedVehiclesLength={usedVehiclesLength.length}
								kamperVanLength={kamperVanLength.length}
								polIntegraLength={polIntegraLength.length}
								integraLength={integraLength.length}
								handlePage={handlePage}
								brands={sortedBrands}
								brandsCounts={brandsCounter}
								showUsedOnStart={conditions.includes("used")}
							/>
						</div>
					</div>
				</div>
			</section>
		</Layout>
	);
};

export const query = graphql`
	query MyQuery {
		vehicles: allWpVehicle(
			sort: { order: DESC, fields: date }
			filter: { vehicles: { groupOrSingle: { eq: "single" } } }
		) {
			edges {
				node {
					slug
					uri
					title
					date
					vehicleType {
						vehicleType
					}
					vehicles {
						available
						discount
						gallery {
							sourceUrl
						}
						labelColor
						labelName
						name
						price
						priceDiscount
						shortDesc
						showLabel
						used
						type
						category {
							name
						}
					}
					vehiclesBrand {
						nodes {
							name
						}
					}
				}
			}
		}
		trailers: allWpTrailer(
			filter: { vehicles: { groupOrSingle: { eq: "single" } } }
		) {
			edges {
				node {
					slug
					uri
					title
					date
					vehicleType {
						vehicleType
					}
					vehicles {
						available
						discount
						labelColor
						labelName
						shortDesc
						name
						gallery {
							sourceUrl
						}
						price
						priceDiscount
						showLabel
						type
						used
						category {
							name
						}
					}
					trailersBrand {
						nodes {
							name
						}
					}
				}
			}
		}
		allWpVehicleBrand {
			nodes {
				name
			}
		}
		allWpTrailerBrand {
			nodes {
				name
			}
		}
	}
`;

export default PageAllVehicles;
